import React, { useEffect, useState } from 'react'
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material'
import TextInputComponent, { TextFieldType } from '../../../shared/components/TextInputComponent'
import ButtonComponent from '../../../shared/components/ButtonComponent'
import Env from '../../../shared/config/envConfig'
import { setMessage, setUser } from '../../../store/settings/settings.actions'
import { Severity, User } from '../../../shared/types'
import { useHistory } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { getUser } from '../../../store/settings/settings.selectors'

interface Props {
  showDialog: boolean
  setShowDialog: (value) => void
}

const ResetPassword: React.FC<Props> = (props) => {
  const history = useHistory()
  const dispatch = useDispatch()

  const user: User | undefined = useSelector(getUser)

  const [oldPassword, setOldPassword] = useState<string | undefined>()
  const [newPassword, setNewPassword] = useState<string | undefined>()
  const [confirmPassword, setConfirmPassword] = useState<string | undefined>()
  const [showHint, setShowHint] = useState<boolean>(false)
  const [showDialog, setShowDialog] = useState<boolean>(props.showDialog)

  useEffect(() => {
    setShowDialog(props.showDialog)
  }, [props.showDialog],
  )

  return (
    <Dialog
      open={showDialog} onClose={() => props.setShowDialog(false)}
      sx={{ '& .MuiDialog-paper': { borderRadius: 0, minWidth: '400px', maxWidth: '600px' } }}
    >
      <Stack sx={{ p: 3 }}>
        <Stack
          direction='row' spacing={3} alignItems='center'
          justifyContent='space-between'
        >
          <DialogTitle sx={{ fontWeight: 600, p: 0, fontSize: '16px', marginBottom: '20px' }}>
            Passwort ändern
          </DialogTitle>
        </Stack>
        <DialogContent sx={{ p: 0, overflow: 'visible' }}>
          <Typography variant='body1'>Bitte gib nachfolgend dein neues Passwort ein und wiederhole es.<br />
            Dein neues Passwort muss:
          </Typography>
          <ul>
            <li>
              mindestens aus 8 Zeichen bestehen,
            </li>
            <li>
              mindestens einen Buchstaben enthalten,
            </li>
            <li>
              mindestens eine Zahl enthalten
            </li>
            <li>
              und mindestens ein Sonderzeichen (@$!%*#?&) enthalten.
            </li>
          </ul>
          <Box sx={{ width: '300px' }}>
            <TextInputComponent
              size='small'
              type={TextFieldType.password}
              label='Altes Passwort'
              value={oldPassword}
              required
              onChange={(value) => setOldPassword(value)}
            />
          </Box>
          <Box sx={{ width: '300px' }}>
            <TextInputComponent
              helperText='Das Passwort entspricht nicht den Richtlinien!'
              showError={showHint}
              size='small'
              type={TextFieldType.password}
              label='Neues Passwort'
              value={newPassword}
              required
              onChange={(value) => {
                setShowHint(false)
                setNewPassword(value)
              }}
            />
          </Box>
          <Box sx={{ width: '300px' }}>
            <TextInputComponent
              helperText='Das wiederholte Passwort stimmt nicht überein.'
              showError={!!((newPassword && newPassword !== confirmPassword))}
              size='small'
              type={TextFieldType.password}
              label='Neues Password wiederholen'
              value={confirmPassword}
              required
              onChange={(value) => setConfirmPassword(value)}
            />
          </Box>
        </DialogContent>
      </Stack>
      <DialogActions sx={{ p: 3 }}>
        <ButtonComponent
          color='primary'
          variant='text'
          text='Abbrechen'
          onClick={() => {
            props.setShowDialog(false)
          }}
        />
        <ButtonComponent
          size='medium'
          fullWidth
          disabled={!newPassword || !confirmPassword || newPassword !== confirmPassword}
          text='Passwort ändern'
          variant='text'
          onClick={() => {
            // checkPassword
            if (newPassword?.match(/^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/) && user) {
              setShowHint(false)
              const fetchHeaders = Env.fetchHeaders
              const basePath = Env.restBase
              const restUrl = `${basePath}/api/user/${user.userId}/changePassword`
              fetch(restUrl,
                {
                  method: 'PUT',
                  credentials: 'include',
                  headers: fetchHeaders,
                  body: JSON.stringify({
                    userId: user.userId,
                    oldPassword: oldPassword,
                    newPassword: newPassword,
                  }),
                })
                .then(res => {
                  setMessage({ message: 'Passwort wurde geändert', severity: Severity.success })
                  setNewPassword(undefined)
                  setConfirmPassword(undefined)

                  const restUrl = `${basePath}/api/session/logout`
                  fetch(restUrl,
                    {
                      method: 'POST',
                      credentials: 'include',
                      headers: fetchHeaders,
                    })
                    .then(() => {
                      history.push('/login')
                      dispatch(setUser(undefined))
                    })
                    .catch(err => console.log(err))
                })
                .catch(() => {
                  setMessage({
                    message: 'Ein unerwarteter Fehler. Bitte erneut versuchen.',
                    severity: Severity.error,
                  })
                })
            } else {
              setShowHint(true)
            }
          }}
        />
      </DialogActions>
    </Dialog>
  )
}

export default ResetPassword
